// BrandingContext — fetches branding once and applies it app-wide via CSS variables.
// Overrides --navy (primary) and --gold (accent), exposes logo_url + app_name for components.
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

const DEFAULTS = {
  app_name: 'Contract | HotelPulse',
  logo_url: '/gnosis-logo.png',
  primary_color: '#1F3864',
  accent_color: '#C9A84C',
  sidebar_text_color: '#ffffff',
  heading_color: '#1F3864',
  footer_text: '',
  logo_height: 48,
  logo_fit: 'contain',
};

const BrandingContext = createContext({ branding: DEFAULTS, refresh: () => {} });

// Compute a lighter / darker sibling for a hex color (for --navy-light, --gold-light etc.)
const shade = (hex, percent) => {
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + Math.round(255 * percent)));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * percent)));
  const b = Math.max(0, Math.min(255, (num & 0xff) + Math.round(255 * percent)));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
};

// Convert hex → rgba with alpha (for pale backgrounds)
const withAlpha = (hex, alpha) => {
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const r = (parseInt(h, 16) >> 16) & 0xff;
  const g = (parseInt(h, 16) >> 8) & 0xff;
  const b = parseInt(h, 16) & 0xff;
  return `rgba(${r},${g},${b},${alpha})`;
};

const applyBrandingToDOM = (b) => {
  const root = document.documentElement;
  const primary = b.primary_color || DEFAULTS.primary_color;
  const accent = b.accent_color || DEFAULTS.accent_color;
  const sidebarText = b.sidebar_text_color || DEFAULTS.sidebar_text_color;
  const headingCol = b.heading_color || primary;
  root.style.setProperty('--navy', primary);
  root.style.setProperty('--navy-light', shade(primary, 0.1));
  root.style.setProperty('--navy-dark', shade(primary, -0.1));
  root.style.setProperty('--gold', accent);
  root.style.setProperty('--gold-light', shade(accent, 0.15));
  root.style.setProperty('--gold-pale', withAlpha(accent, 0.12));
  root.style.setProperty('--sidebar-text', sidebarText);
  root.style.setProperty('--sidebar-text-muted', withAlpha(sidebarText, 0.65));
  root.style.setProperty('--sidebar-text-dim', withAlpha(sidebarText, 0.35));
  root.style.setProperty('--sidebar-hover', withAlpha(sidebarText, 0.09));
  root.style.setProperty('--heading', headingCol);
  if (b.app_name) document.title = b.app_name;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/branding`);
      const merged = { ...DEFAULTS, ...r.data };
      setBranding(merged);
      applyBrandingToDOM(merged);
    } catch (_err) {
      applyBrandingToDOM(DEFAULTS);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ branding, refresh }), [branding, refresh]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
